/**
 * @module config/editions
 * @description The two editions of the product, as data.
 *
 * - **gala** — the original vision for Gala night (Fri Oct 16, 2026): live
 *   1:1 matching-gift challenge, "tonight" language, Gala postmark, ballroom
 *   screen and the paper pledge card on the back of every postcard. Kept
 *   complete so AFJ can roll back to it at any time.
 * - **campaign** — the post-Gala, use-any-day edition from Samantha's notes
 *   (2026-10-01): no Gala-night or matching language anywhere, "Our Love
 *   Challenge" counts friends who have given, totals show only real dollars,
 *   and postcards carry a mailable back with a per-card QR code.
 *
 * Every component reads its wording from the active edition, so switching
 * editions is a data change, not a code change (Open/Closed). See
 * app/edition.ts for how the active edition is chosen.
 */
import type { EditionId, PaymentIntent } from "../domain/types";
import { CAMPAIGN, formatUsd } from "./campaign";

/** One option in section 2 of the pledge form, with this edition's wording. */
export interface IntentOption {
    intent: PaymentIntent;
    label: string;
}

export interface EditionConfig {
    id: EditionId;
    /** Admin label. */
    label: string;
    /** One-line admin description. */
    summary: string;
    /** Shows the matching-gift challenge (meter, doubling math, "unlock" language). */
    showMatch: boolean;
    /** Which postmark the cards carry. */
    postmark: "gala" | "campaign";
    /** Lists the ballroom projection view in the navigation. */
    showBallroomScreen: boolean;
    /** What prints on the back of each story postcard. */
    storyCardBack: "pledge" | "mail";
    /** Heading of the progress panel. */
    challengeHeading: string;
    nav: { giveLabel: string };
    hero: { kicker: string; lede: string; ledeEmphasis: string; primaryCta: string; secondaryCta: string };
    card: {
        /** Line under "This is your love at work." on every card front. */
        footerSub: string;
        /** Button under a single card. */
        ctaLabel: string;
        /** Sentence appended to share text. Empty for none. */
        shareSuffix: string;
    };
    pledge: {
        /** Small-caps line under the headline. Empty = none. */
        asideKicker: string;
        asideHeadline: string;
        asideLede: string;
        asideTagline: string;
        /** Legend of section 1. */
        amountLegend: string;
        presetAmounts: readonly number[];
        intents: readonly IntentOption[];
        defaultIntent: PaymentIntent;
        showTableNumber: boolean;
        /** Requires "I agree to the Terms and Conditions" before submitting. */
        requireTerms: boolean;
        /** Shows the "About your gift" disclosure under the form. */
        showAboutYourGift: boolean;
        submitLabel: (amount: number) => string;
        thanksHeadline: (amount: number) => string;
    };
}

const MATCH = formatUsd(CAMPAIGN.matchCap);

export const EDITIONS: Record<EditionId, EditionConfig> = {
    gala: {
        id: "gala",
        label: "Gala night",
        summary: `Original vision: ${CAMPAIGN.matchRatioLabel} matching gift up to ${MATCH}, Gala postmark, pledge card on the back.`,
        showMatch: true,
        postmark: "gala",
        showBallroomScreen: true,
        storyCardBack: "pledge",
        challengeHeading: `${CAMPAIGN.matchRatioLabel} Matching Gift Challenge`,
        nav: { giveLabel: "Give tonight" },
        hero: {
            kicker: `${CAMPAIGN.eventName} · ${CAMPAIGN.eventVenue}`,
            lede: `Tonight every gift is matched, dollar for dollar, up to ${MATCH} by a friend of AFJ who loves Jamaica as much as you do.`,
            ledeEmphasis: "Your gift goes twice as far.",
            primaryCta: "Double my gift",
            secondaryCta: "See your love at work",
        },
        card: {
            footerSub: `Tonight every gift is matched, up to ${MATCH}. Turn over to give.`,
            ctaLabel: "Turn over to give →",
            shareSuffix: `Tonight every gift is matched up to ${MATCH}.`,
        },
        pledge: {
            asideKicker: "Matching gift challenge",
            asideHeadline: MATCH,
            asideLede: `Every dollar given tonight is matched ${CAMPAIGN.matchRatioLabel}, up to ${MATCH}, by a friend of AFJ who loves Jamaica as much as you do.`,
            asideTagline: "Your gift goes twice as far.",
            amountLegend: "Yes, I'll help unlock the match",
            presetAmounts: [250, 500, 1_000, 2_500, 5_000, 10_000, 25_000],
            intents: [
                { intent: "pay-tonight", label: "Paid tonight via the QR code" },
                { intent: "pledge-by-deadline", label: `Pledge, paid by ${CAMPAIGN.pledgeDeadlineLabel}` },
                { intent: "daf-grant", label: "I intend to recommend a DAF grant" },
                { intent: "call-me", label: "Please call me to arrange my gift" },
            ],
            defaultIntent: "pay-tonight",
            showTableNumber: true,
            requireTerms: false,
            showAboutYourGift: false,
            submitLabel: (amount) => (amount > 0 ? `Pledge ${formatUsd(amount)} — unlock ${formatUsd(amount)} more` : "Pledge my gift"),
            thanksHeadline: (amount) => `Your ${formatUsd(amount)} just became ${formatUsd(amount * 2)} for Jamaica.`,
        },
    },
    campaign: {
        id: "campaign",
        label: "Year-round campaign",
        summary: "Post-Gala, any-day edition: no Gala-night or matching language; Our Love Challenge; mailable postcard backs.",
        showMatch: false,
        postmark: "campaign",
        showBallroomScreen: false,
        storyCardBack: "mail",
        challengeHeading: "Our Love Challenge",
        nav: { giveLabel: "Give" },
        hero: {
            kicker: CAMPAIGN.organizationName,
            // Copy from Samantha's giving-page draft (2026-10-01).
            lede: "Every gift made through To Jamaica, With Love goes where Jamaica needs it most, from the next school roof to a child's first eye exam.",
            ledeEmphasis: "Put your love for Jamaica to work.",
            primaryCta: "Give to Jamaica",
            secondaryCta: "See your love at work",
        },
        card: {
            footerSub: `Put your love for Jamaica to work at ${CAMPAIGN.siteLabel}.`,
            ctaLabel: "Give to Jamaica →",
            shareSuffix: "",
        },
        pledge: {
            // No kicker: the Our Love Challenge panel directly below already carries that heading.
            asideKicker: "",
            asideHeadline: "Put your love for Jamaica to work.",
            asideLede: "Every gift made through To Jamaica, With Love goes where Jamaica needs it most, from the next school roof to a child's first eye exam.",
            asideTagline: "This is your love at work.",
            amountLegend: "Choose your gift",
            presetAmounts: [25, 50, 100, 250, 500],
            intents: [
                { intent: "give-online", label: "Give securely online" },
                { intent: "daf-grant", label: "Give through a donor-advised fund" },
                { intent: "mail-check", label: "Mail a check" },
                { intent: "call-me", label: "Please call me" },
            ],
            defaultIntent: "give-online",
            showTableNumber: false,
            requireTerms: true,
            showAboutYourGift: true,
            submitLabel: () => "Continue",
            thanksHeadline: () => "Thank you for putting your love for Jamaica to work.",
        },
    },
};

/** Build-time default. Set VITE_AFJ_EDITION=gala in the build environment to ship the Gala edition. */
export const DEFAULT_EDITION_ID: EditionId = "campaign";

/** Type guard for untrusted input (URL parameters, storage, env). */
export const isEditionId = (value: unknown): value is EditionId => value === "gala" || value === "campaign";
